import { motion } from "framer-motion";
import { GearSix, Wrench, Lightning, ShieldCheck } from "@phosphor-icons/react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function SpecialtyEquipment() {
  return (
    <div>
      {/* HERO */}
      <section className="relative py-24 lg:py-32 bg-[#0B2D2D] overflow-hidden noise-overlay" data-testid="specialty-hero">
        <div className="absolute inset-0 pattern-grid opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Products / Specialty Equipment</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.9] mb-6">
              Specialty<br />Equipment
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-[#E0F5F2]/70 max-w-2xl leading-relaxed">
              Custom-designed and fabricated specialty equipment for the road construction and industrial sectors. Engineered in-house and built to exacting standards.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* COLD MIX PLANT */}
      <section className="py-20 lg:py-28 bg-white" data-testid="cold-mix-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Specialty Plant 01</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D] mb-4">10TPH Cold Mix Asphalt Plant</h2>
            <p className="text-base text-[#0B2D2D]/50 font-mono mb-10">Bitumen / Asphalt Handling Plant</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <motion.div className="lg:col-span-7" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#0F4C5C]/20 p-8 lg:p-10">
                {/* Project Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: "Capacity", value: "10 TPH" },
                    { label: "Location", value: "Carletonville, Gauteng" },
                    { label: "Scope", value: "Design, Fabrication & Installation" },
                    { label: "Type", value: "Cold Mix Asphalt" },
                  ].map((d, i) => (
                    <div key={i} className="border border-[#0F4C5C]/10 p-3">
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#00A091] mb-1">{d.label}</p>
                      <p className="text-sm font-semibold text-[#0B2D2D]">{d.value}</p>
                    </div>
                  ))}
                </div>

                <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-4">Design &amp; Engineering</h3>
                <p className="text-sm text-[#0B2D2D]/70 leading-relaxed mb-6">
                  Designed in-house to meet specific production requirements for cold mix asphalt, ensuring optimal mixing ratios and throughput. The plant was engineered for road material processing with modular components for ease of transport and installation.
                </p>

                <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-4">Key Components</h3>
                <div className="space-y-3 mb-6">
                  {[
                    { icon: <GearSix size={18} weight="duotone" />, text: "Aggregate feeding systems" },
                    { icon: <GearSix size={18} weight="duotone" />, text: "Bitumen emulsion tanks" },
                    { icon: <GearSix size={18} weight="duotone" />, text: "Mixing drums" },
                    { icon: <GearSix size={18} weight="duotone" />, text: "Control systems" },
                  ].map((c, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-[#00A091]">{c.icon}</span>
                      <span className="text-sm text-[#0B2D2D]/80">{c.text}</span>
                    </div>
                  ))}
                </div>

                <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-4">Fabrication &amp; Installation</h3>
                <p className="text-sm text-[#0B2D2D]/70 leading-relaxed">
                  Key components were fabricated as transportable modules in our workshop, then transported to the Carletonville site. Full site setup and installation was managed by KZNSC, ensuring components were calibrated and commissioned for immediate operation.
                </p>
              </div>
            </motion.div>

            <motion.div className="lg:col-span-5 space-y-6" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="img-zoom border border-[#0F4C5C]/20">
                <img
                  src="/images/projects/cold-mix-plant.jpeg"
                  alt="10TPH Cold Mix Asphalt Plant"
                  className="w-full h-[300px] object-cover"
                />
              </div>
              <div className="img-zoom border border-[#0F4C5C]/20">
                <img
                  src="/images/projects/cold-mix-plant.jpeg"
                  alt="Cold Mix Plant machinery detail"
                  className="w-full h-[300px] object-cover object-left"
                />
              </div>
              <div className="bg-[#0F4C5C] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A7D129] mb-2">Highlights</p>
                <ul className="space-y-2">
                  {["Custom in-house design", "Modular fabrication", "On-site commissioning", "10 ton per hour capacity"].map((h, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#E0F5F2]/80">
                      <div className="w-1.5 h-1.5 bg-[#A7D129] flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DEBAGGING PLANT */}
      <section className="py-20 lg:py-28 bg-[#E0F5F2]" data-testid="debagging-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Specialty Plant 02</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D] mb-4">Sugar Debagging Unit</h2>
            <p className="text-base text-[#0B2D2D]/50 font-mono mb-10">Bulk Material Debagging &amp; Handling Plant</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <motion.div className="lg:col-span-5 order-2 lg:order-1 space-y-6" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="img-zoom border border-[#0F4C5C]/20">
                <img src="/images/projects/sugar-debagging-unit.jpeg" alt="Sugar Debagging Unit" className="w-full h-[280px] object-cover" />
              </div>
              <div className="bg-[#E0F5F2] border border-[#0F4C5C]/20 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00A091] font-bold mb-3">Project Scope</p>
                <p className="text-sm text-[#0B2D2D]/70 leading-relaxed">
                  Full Refurbishment, Safety Upgrades, and Electrical / Mechanical Optimization of an existing bulk material debagging and handling plant.
                </p>
              </div>
              <div className="bg-[#0B2D2D] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A7D129] mb-3">Outcome</p>
                <p className="text-sm text-[#E0F5F2]/70 leading-relaxed">
                  Extended plant lifespan and enhanced operational performance through comprehensive mechanical, safety, and electrical improvements.
                </p>
              </div>
            </motion.div>

            <motion.div className="lg:col-span-7 order-1 lg:order-2" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-[#E0F5F2] border border-[#0F4C5C]/20 p-8 lg:p-10">
                <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-6">Key Improvements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { icon: <Wrench size={20} weight="duotone" />, title: "Vibrator System", desc: "High-performance vibrator system to improve material flow and throughput in hoppers." },
                    { icon: <ShieldCheck size={20} weight="duotone" />, title: "Structural Repair", desc: "Full structural repair and reinforcement of all existing hoppers." },
                    { icon: <GearSix size={20} weight="duotone" />, title: "New Motors", desc: "Installation of new motors for reliable and efficient plant operation." },
                    { icon: <Wrench size={20} weight="duotone" />, title: "Catwalks & Platforms", desc: "Design, fabrication, and erection of new catwalks and additional platforms for safe monitoring access." },
                    { icon: <ShieldCheck size={20} weight="duotone" />, title: "Safety Systems", desc: "Implementation of E-stops and sirens. Installation of new DB board." },
                    { icon: <Lightning size={20} weight="duotone" />, title: "Electrical Overhaul", desc: "Complete replacement and installation of all new electrical components including wiring, controls, and switches." },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-[#0F4C5C]/10 p-4" data-testid={`debagging-improvement-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[#00A091]">{item.icon}</span>
                        <h4 className="font-heading font-bold text-sm text-[#0B2D2D]">{item.title}</h4>
                      </div>
                      <p className="text-xs text-[#0B2D2D]/60 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
